import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { locale, useAppContext } from '../../contexts/AppContext';
import { useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ChartHelpTooltip from '../common/ChartHelpTooltip';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { CalendarSeries, DateValue } from '../../types';
import { buildTypicalSoilingYearOptions } from '../../utils/chartOptions/typicalSoilingYear';

interface SoilingRatioData {
  daily: { times: string[]; values: Record<string, (number | null)[]> };
  monthly: { times: string[]; values: { soiling_ratio: number[] } };
  yearly: { values: { soiling_ratio: number[] } };
}

type Props = {
  data: SoilingRatioData;
  yearly_cleaning_days: DateValue[];
  rain_threshold: number;
  ymin: number;
  ymax: number;
};

const SoilingRatio = ({ data, yearly_cleaning_days, rain_threshold, ymin, ymax }: Props) => {
  const { isDarkMode } = useAppContext();
  const chartComponent = useRef(null);
  const [copiedText, setCopiedText] = useState(false);
  const chartOptions = useMemo(
    () =>
      Object.prototype.hasOwnProperty.call(data, 'daily') &&
      Object.prototype.hasOwnProperty.call(data.daily, 'values') &&
      Object.prototype.hasOwnProperty.call(data.daily, 'times')
        ? buildTypicalSoilingYearOptions(data as unknown as CalendarSeries, {
            yearlyCleaningDays: yearly_cleaning_days,
            rainThreshold: rain_threshold,
            ymin,
            ymax,
          })
        : null,
    [data],
  );

  const handleTextCopy = (text: string[]) => {
    copyText(text);
    setCopiedText(true);
    setTimeout(() => {
      setCopiedText(false);
    }, 1500);
  };

  const copyText = (text: string[]) => {
    let txt = 'Month\tSoiling Ratio\tSoiling Level\n';
    text.forEach((t: string, index: number) => {
      const d = new Date(Date.parse(t));
      const ts = d.toLocaleString(locale, { month: 'long' });
      txt +=
        ts +
        '\t' +
        Math.round(data.monthly?.values?.soiling_ratio[index] * 10000) / 10000 +
        '\t' +
        Math.round((1 - data.monthly?.values?.soiling_ratio[index]) * 10000) / 10000 +
        '\n';
    });
    navigator.clipboard.writeText(txt);
  };

  return (
    <>
      <Box sx={{ justifyContent: 'center', pl: 1, pr: 1 }}>
        <>
          <Box display="flex" alignItems="center">
            <Typography variant="h5" color="text.primary" sx={{ mt: 0 }}>
              Soiling Ratio
            </Typography>
            <ChartHelpTooltip chartType="soilingRatio" />
          </Box>
          {chartOptions && (
            <HighchartsReact
              ref={chartComponent}
              containerProps={{ className: isDarkMode ? 'dark-chart' : '', style: { width: '100%' } }}
              highcharts={Highcharts}
              options={chartOptions}
            />
          )}
          <Typography variant="h5" noWrap color="text.primary" sx={{ mt: 1 }}>
            Yearly Average Soiling Ratio
          </Typography>
          <Typography>Soiling Ratio: {Math.round(data.yearly?.values?.soiling_ratio[0] * 10000) / 100} %</Typography>
          <Typography>Soiling Level: {Math.round((1 - data.yearly?.values?.soiling_ratio[0]) * 10000) / 100} %</Typography>
          <Typography variant="h5" noWrap color="text.primary" sx={{ mt: 1 }}>
            Monthly Average Soiling Ratio
            <Tooltip title={copiedText ? 'Table copied!' : 'Copy table'}>
              <IconButton
                onClick={() => {
                  handleTextCopy(data.monthly?.times);
                }}
              >
                {copiedText ? <CheckCircleIcon fontSize="small" sx={{ color: '#008000' }} /> : <ContentCopyRoundedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table sx={{ minWidth: 650 }} size="small" aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Soiling Ratio (%)</TableCell>
                  <TableCell align="right">Soiling Level (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.monthly?.times?.map((t: string, index: number) => {
                  const d = new Date(Date.parse(t));
                  const ts = d.toLocaleString(locale, { month: 'long' });
                  const soiling_ratio = Math.round(data.monthly?.values?.soiling_ratio[index] * 10000) / 100;
                  const soiling_level = Math.round((1 - data.monthly?.values?.soiling_ratio[index]) * 10000) / 100;
                  return (
                    <TableRow key={t} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        {ts}
                      </TableCell>
                      <TableCell align="right">{soiling_ratio}</TableCell>
                      <TableCell align="right">{soiling_level}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      </Box>
    </>
  );
};

export default SoilingRatio;
